import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { API } from './adminUtils';

function SeoInput({ label, value, onChange, testid, hint, className = '' }) {
  return (
    <div className={className}>
      <Label>{label}</Label>
      <Input value={value} onChange={e => onChange(e.target.value)} className="mt-1" data-testid={testid} />
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function SeoTextarea({ label, value, onChange, testid, hint, rows = 3, className = '' }) {
  return (
    <div className={className}>
      <Label>{label}</Label>
      <Textarea value={value} onChange={e => onChange(e.target.value)} rows={rows} className="mt-1 resize-none" data-testid={testid} />
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

export function SeoTab({ seoForm, setSeoForm, handleSeoUpload, handleSeoSave, seoSaving }) {
  if (!seoForm) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 max-w-3xl">
      <div>
        <h3 className="text-lg font-bold text-slate-800 mb-1">Site Kimliği</h3>
        <p className="text-xs text-slate-500 mb-3">Google ve sosyal paylaşımlarda görünecek genel site bilgileri</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <SeoInput label="Site Adı" value={seoForm.site_name || ''} onChange={v => setSeoForm(p => ({...p, site_name: v}))} testid="seo-site-name" />
          <SeoInput label="Başlık Soneki" value={seoForm.title_suffix || ''} onChange={v => setSeoForm(p => ({...p, title_suffix: v}))} testid="seo-title-suffix" hint="örn: ' · MiniOto'" />
          <SeoInput className="sm:col-span-2" label="Ana Sayfa Başlığı" value={seoForm.home_title || ''} onChange={v => setSeoForm(p => ({...p, home_title: v}))} testid="seo-home-title" />
          <SeoInput className="sm:col-span-2" label="Varsayılan Başlık" value={seoForm.default_title || ''} onChange={v => setSeoForm(p => ({...p, default_title: v}))} testid="seo-default-title" />
          <SeoTextarea className="sm:col-span-2" label="Varsayılan Açıklama" value={seoForm.default_description || ''} onChange={v => setSeoForm(p => ({...p, default_description: v}))} testid="seo-default-desc" rows={2} />
          <SeoInput className="sm:col-span-2" label="Varsayılan Anahtar Kelimeler" value={seoForm.default_keywords || ''} onChange={v => setSeoForm(p => ({...p, default_keywords: v}))} testid="seo-default-kw" hint="virgülle ayırın" />
          <SeoInput className="sm:col-span-2" label="Canonical Base URL" value={seoForm.canonical_base_url || ''} onChange={v => setSeoForm(p => ({...p, canonical_base_url: v}))} testid="seo-canonical" hint="örn: https://minioto.com — boşsa otomatik" />
        </div>
      </div>

      <div className="border-t border-slate-100 pt-5">
        <h3 className="text-lg font-bold text-slate-800 mb-1">Sosyal Paylaşım (Open Graph)</h3>
        <p className="text-xs text-slate-500 mb-3">WhatsApp, Facebook, X, LinkedIn'de link paylaşıldığında görünür</p>
        <div className="space-y-3">
          <div>
            <Label>OG Görseli (1200x630 önerilir)</Label>
            <div className="mt-1 flex items-center gap-3 flex-wrap">
              {seoForm.og_image_url && <img src={seoForm.og_image_url} alt="OG" className="h-14 rounded border border-slate-200" />}
              <Input type="file" accept="image/*" onChange={e => e.target.files?.[0] && handleSeoUpload(e.target.files[0], 'og_image_url')} className="flex-1 min-w-[200px]" data-testid="seo-og-upload" />
              <Input value={seoForm.og_image_url || ''} onChange={e => setSeoForm(p => ({...p, og_image_url: e.target.value}))} placeholder="veya URL yapıştır" className="flex-1 min-w-[200px]" data-testid="seo-og-url" />
            </div>
            <p className="text-xs text-slate-400 mt-1">Boş bırakırsan MiniOto logosu kullanılır</p>
          </div>
          <SeoInput label="Twitter kullanıcı adı" value={seoForm.twitter_handle || ''} onChange={v => setSeoForm(p => ({...p, twitter_handle: v}))} testid="seo-twitter" hint="örn: @minioto" />
        </div>
      </div>

      <div className="border-t border-slate-100 pt-5">
        <h3 className="text-lg font-bold text-slate-800 mb-1">Site İkonları</h3>
        <div className="space-y-3">
          <div>
            <Label>Favicon (32x32 önerilir)</Label>
            <div className="mt-1 flex items-center gap-3 flex-wrap">
              {seoForm.favicon_url && <img src={seoForm.favicon_url} alt="favicon" className="h-8 w-8 rounded border border-slate-200" />}
              <Input type="file" accept="image/*,.ico" onChange={e => e.target.files?.[0] && handleSeoUpload(e.target.files[0], 'favicon_url')} className="flex-1 min-w-[200px]" data-testid="seo-favicon-upload" />
              <Input value={seoForm.favicon_url || ''} onChange={e => setSeoForm(p => ({...p, favicon_url: e.target.value}))} placeholder="veya URL" className="flex-1 min-w-[200px]" data-testid="seo-favicon-url" />
            </div>
          </div>
          <div>
            <Label>Apple Touch Icon (180x180)</Label>
            <div className="mt-1 flex items-center gap-3 flex-wrap">
              {seoForm.apple_touch_icon_url && <img src={seoForm.apple_touch_icon_url} alt="apple" className="h-10 w-10 rounded border border-slate-200" />}
              <Input type="file" accept="image/*" onChange={e => e.target.files?.[0] && handleSeoUpload(e.target.files[0], 'apple_touch_icon_url')} className="flex-1 min-w-[200px]" data-testid="seo-apple-upload" />
              <Input value={seoForm.apple_touch_icon_url || ''} onChange={e => setSeoForm(p => ({...p, apple_touch_icon_url: e.target.value}))} placeholder="veya URL" className="flex-1 min-w-[200px]" data-testid="seo-apple-url" />
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-100 pt-5">
        <h3 className="text-lg font-bold text-slate-800 mb-1">Kurum / Organization Şeması</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <SeoInput label="Kurum Adı" value={seoForm.organization_name || ''} onChange={v => setSeoForm(p => ({...p, organization_name: v}))} testid="seo-org-name" />
          <SeoInput label="Kurum URL" value={seoForm.organization_url || ''} onChange={v => setSeoForm(p => ({...p, organization_url: v}))} testid="seo-org-url" />
        </div>
      </div>

      <div className="border-t border-slate-100 pt-5">
        <h3 className="text-lg font-bold text-slate-800 mb-1">Google Araçları</h3>
        <p className="text-xs text-slate-500 mb-3">Search Console doğrulaması (DNS doğrulaması yaptıysanız boş bırakabilirsiniz) ve Analytics</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <SeoInput label="Google Site Verification" value={seoForm.google_site_verification || ''} onChange={v => setSeoForm(p => ({...p, google_site_verification: v}))} testid="seo-google-verify" hint="Meta etiket değeri (ör: abc123xyz) — DNS doğruladıysanız gerekmez" />
          <SeoInput label="Google Analytics (GA4) ID" value={seoForm.google_analytics_id || ''} onChange={v => setSeoForm(p => ({...p, google_analytics_id: v}))} testid="seo-ga-id" hint="örn: G-XXXXXXX" />
        </div>
      </div>

      <div className="border-t border-slate-100 pt-5">
        <h3 className="text-lg font-bold text-slate-800 mb-1">Index / Noindex Kuralları</h3>
        <p className="text-xs text-slate-500 mb-3">Her satıra bir yol başlangıcı yaz: ör. <code>/admin</code>, <code>/profil/</code>, <code>/garaj</code>. Bu yollarda noindex meta eklenir.</p>
        <SeoTextarea
          label="Noindex Yolları"
          value={Array.isArray(seoForm.noindex_paths) ? seoForm.noindex_paths.join('\n') : (seoForm.noindex_paths || '')}
          onChange={v => setSeoForm(p => ({...p, noindex_paths: v}))}
          testid="seo-noindex-paths"
          rows={4}
        />
      </div>

      <div className="border-t border-slate-100 pt-5 flex items-center justify-between flex-wrap gap-3">
        <a href={`${API}/sitemap.xml`} target="_blank" rel="noreferrer" className="text-sm text-emerald-700 hover:text-emerald-800 font-medium" data-testid="seo-sitemap-link">
          Sitemap XML'i görüntüle →
        </a>
        <Button onClick={handleSeoSave} disabled={seoSaving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="seo-save-btn">
          {seoSaving ? 'Kaydediliyor...' : 'SEO Ayarlarını Kaydet'}
        </Button>
      </div>
    </div>
  );
}
